/**
 * Chữ hiển thị của cụm "Quản lý điểm thưởng" (`/quan-ly-diem-thuong`, PROD) — danh sách thành viên +
 * drawer "Chi tiết điểm thưởng" + lịch sử ví. Bảng desktop (`MemberList.js`/`BonusPointHistory.js`)
 * và thẻ điện thoại/iPad (`MemberCard.tsx`/`PointHistoryCard.tsx`) dùng CHUNG các hằng số này thay vì
 * mỗi nơi tự viết literal — Figma FR-W-10 responsive (14/09/2026), Task 6.
 *
 * ⚠️ Đây KHÔNG phải cụm "Nạp thẻ trên ERP" (`constants/topupWizard.ts`) dù cùng đợt responsive —
 * `/quan-ly-diem-thuong` là màn PROD độc lập, chữ cột đã có sẵn TỪ TRƯỚC đợt này. `MEMBER_COLUMN_LABEL`
 * và `POINT_HISTORY_COLUMN_LABEL` chỉ GOM lại chữ cột đã tồn tại (`MemberList.js`/`BonusPointHistory.js`),
 * KHÔNG đổi một ký tự nào.
 */

/** Tiêu đề cột bảng thành viên (`MemberList.js`) — CHỮ GIỮ NGUYÊN như bản cũ. */
export const MEMBER_COLUMN_LABEL = {
  fullName: 'Họ và tên',
  username: 'Tên tài khoản',
  role: 'Loại tài khoản',
  point: 'Tổng điểm tích luỹ',
  status: 'Trạng thái',
} as const

/** Tiêu đề cột bảng lịch sử ví (`BonusPointHistory.js`) — CHỮ GIỮ NGUYÊN như bản cũ. */
export const POINT_HISTORY_COLUMN_LABEL = {
  time: 'Thời gian',
  type: 'Hình thức',
  detail: 'Chi tiết',
  point: 'Số điểm',
  amount: 'Thành tiền',
  status: 'Trạng thái',
} as const

/**
 * Tiêu đề cột bảng "Yêu cầu đổi điểm" (`requestChangePoint.js`) — bảng desktop và thẻ điện thoại/iPad (`ExchangeRequestCard.tsx`)
 * dùng chung (Task 10). CHỮ GIỮ NGUYÊN như bản cũ. Năm chữ TRÙNG bảng thành viên trỏ về đúng khoá của `MEMBER_COLUMN_LABEL`,
 * "Thành tiền" trỏ về `POINT_HISTORY_COLUMN_LABEL` — một chữ chỉ khai MỘT chỗ trong file; riêng "Số điểm đổi" là chữ của bảng này.
 */
export const EXCHANGE_REQUEST_COLUMN_LABEL = {
  fullName: MEMBER_COLUMN_LABEL.fullName,
  username: MEMBER_COLUMN_LABEL.username,
  role: MEMBER_COLUMN_LABEL.role,
  totalPoint: MEMBER_COLUMN_LABEL.point,
  exchangePoint: 'Số điểm đổi',
  amount: POINT_HISTORY_COLUMN_LABEL.amount,
  status: MEMBER_COLUMN_LABEL.status,
} as const

/** Nhãn đọc (`MobileCardList` `ariaLabel`) cho danh sách thẻ yêu cầu đổi điểm trên điện thoại/iPad. */
export const EXCHANGE_REQUEST_LIST_ARIA_LABEL = 'Danh sách yêu cầu đổi điểm'

/** Tiền tố nhãn đọc khi bấm mở một thẻ yêu cầu đổi điểm (ghép với họ tên) — cùng khuôn `MEMBER_CARD_OPEN_LABEL`. */
export const EXCHANGE_REQUEST_CARD_OPEN_LABEL = 'Xem chi tiết yêu cầu đổi điểm'

/** `aria-label` của hai nút icon ✓/× (cột action bảng + thẻ) — nút chỉ có icon, không chữ hiển thị. */
export const EXCHANGE_REQUEST_APPROVE_LABEL = 'Duyệt'
export const EXCHANGE_REQUEST_REJECT_LABEL = 'Từ chối'

/** Nhãn đọc (`MobileCardList` `ariaLabel`) cho danh sách thẻ thành viên trên điện thoại/iPad. */
export const MEMBER_LIST_ARIA_LABEL = 'Danh sách thành viên'

/** Tiền tố nhãn đọc khi bấm mở một thẻ thành viên (ghép với họ tên) — cùng khuôn `TOPUP_ORDER_OPEN_DETAIL_LABEL`. */
export const MEMBER_CARD_OPEN_LABEL = 'Xem chi tiết thành viên'

/** Nhãn đọc (`MobileCardList` `ariaLabel`) cho danh sách thẻ lịch sử ví trên điện thoại/iPad. */
export const POINT_HISTORY_LIST_ARIA_LABEL = 'Lịch sử ví'

/** Trang đang xem của một tab "Quản lý điểm thưởng" — tab con báo lên trang cha qua `onRangeChange`. */
export interface BonusPointListRange {
  page: number
  pageSize: number
  /** `meta.total` của API — `undefined`/không phải số (API thiếu trường) coi như 0. */
  total: number | null | undefined
}

/** Tab chưa báo số (chưa mở lần nào / chưa tải xong lượt đầu). */
export const EMPTY_BONUS_POINT_RANGE: BonusPointListRange = { page: 1, pageSize: 0, total: 0 }

/**
 * Mặc định của prop `onRangeChange` ở `MemberList.js`/`requestChangePoint.js` khi nơi dùng không cần dòng "Hiển thị": một hàm
 * CẤP MODULE (định danh ổn định — hai tab đặt prop này vào dependency của effect báo dải, hàm mới mỗi lượt render là chạy lại).
 */
export const IGNORE_BONUS_POINT_RANGE = (range: BonusPointListRange): void => undefined

/**
 * Dòng dưới tiêu đề "Quản lý điểm thưởng": `Hiển thị {from}-{to} của {total}` — chữ đúng Figma FR-W-10 ("Hiển thị 1-10 của
 * 100"), KHÔNG kèm danh từ đếm; hiện ở MỌI bề rộng, theo TAB ĐANG MỞ (người dùng chốt 14/09/2026, Task 9).
 * `total` 0 ⇒ `Hiển thị 0-0 của 0`; `to` = min(page × pageSize, total); `from` = min((page − 1) × pageSize + 1, to) — trang
 * còn đứng quá trang cuối (tổng vừa co lại, chưa kịp về trang hợp lệ) không in "41-3".
 */
export const bonusPointRangeLabel = ({ page, pageSize, total }: BonusPointListRange): string => {
  const safeTotal = typeof total === 'number' && Number.isFinite(total) && total > 0 ? total : 0
  const to = Math.min(page * pageSize, safeTotal)
  const from = safeTotal === 0 ? 0 : Math.min((page - 1) * pageSize + 1, to)
  return `Hiển thị ${from}-${to} của ${safeTotal}`
}
